import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, PackageSearch } from "lucide-react";
import { API, img } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import { Reveal } from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

const STEPS = ["Placed", "Confirmed", "Shipped", "Out for Delivery", "Delivered"];

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get("id") || "");
  const [contact, setContact] = useState(params.get("c") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { fmt } = useStore();

  useSEO({ title: "Track Your Order — Just Kords", description: "Track your Just Kords order with your Order ID and email or phone. Live status from placed to delivered." });

  const track = async (e) => {
    if (e) e.preventDefault();
    if (!orderId.trim() || !contact.trim()) { setError("Enter both your Order ID and the email or phone used to order."); setOrder(null); return; }
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/orders/${orderId.trim().toUpperCase()}?contact=${encodeURIComponent(contact.trim())}`);
      setOrder(data); setError("");
    } catch (err) {
      setOrder(null);
      setError(err?.response?.status === 404
        ? "We couldn't find that Order ID. Double-check it (looks like JK-12345)."
        : "That contact doesn't match this order. Try the email or phone used at checkout.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (params.get("id") && params.get("c")) track();
    // eslint-disable-next-line
  }, []);

  const stepIdx = order ? Math.max(STEPS.indexOf(order.status), 0) : -1;

  return (
    <div className="bg-alabaster pt-28 lg:pt-36">
      <div className="mx-auto max-w-3xl px-4 pb-24 sm:px-6">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">Order Tracking</p>
          <h1 className="mt-4 font-serif text-5xl font-light">Where's my kord?</h1>
          <form onSubmit={track} className="mt-8 space-y-4" data-testid="track-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <input data-testid="track-order-id-input" value={orderId} onChange={(e) => setOrderId(e.target.value)}
                placeholder="Order ID (JK-12345)" className="border border-linec bg-white px-4 py-3.5 text-sm focus:border-crimson focus:outline-none" />
              <input data-testid="track-contact-input" value={contact} onChange={(e) => setContact(e.target.value)}
                placeholder="Email or phone used to order" className="border border-linec bg-white px-4 py-3.5 text-sm focus:border-crimson focus:outline-none" />
            </div>
            <button data-testid="track-submit" type="submit" disabled={loading}
              className="inline-flex items-center gap-2 rounded-full bg-crimson px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-crimson-dark disabled:opacity-60">
              <Search size={15} /> {loading ? "Tracking…" : "Track Order"}
            </button>
          </form>
        </Reveal>

        {error && (
          <p data-testid="track-error" className="mt-8 border border-crimson/30 bg-crimson-sub px-5 py-4 text-sm text-crimson">{error}</p>
        )}

        {order && (
          <div className="mt-12" data-testid="track-result">
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-linec pb-5">
              <h2 className="font-serif text-3xl">{order.order_id}</h2>
              <p className="font-mono text-[11px] uppercase tracking-widest text-mutedc">
                Payment: {order.payment_method === "cod" ? "Cash on Delivery" : order.payment_status === "paid" ? "Paid via Razorpay" : "Pending"}
              </p>
            </div>

            <div className="mt-10" data-testid="track-timeline">
              {STEPS.map((s, i) => {
                const done = i <= stepIdx;
                const at = (order.status_history || []).filter((h) => h.status === s).slice(-1)[0];
                return (
                  <div key={s} className="relative flex gap-6 pb-10 last:pb-0">
                    {i < STEPS.length - 1 && (
                      <span className={`absolute left-[13px] top-7 h-full w-0.5 ${i < stepIdx ? "bg-crimson" : "bg-linec"}`} />
                    )}
                    <span className={`relative z-10 mt-1 h-7 w-7 flex-shrink-0 rounded-full border-2 ${done ? "border-crimson bg-crimson" : "border-linec bg-white"}`}>
                      {done && <span className="absolute inset-0 flex items-center justify-center text-[10px] text-white">✓</span>}
                    </span>
                    <div>
                      <p className={`text-sm font-semibold ${done ? "text-ink" : "text-mutedc"}`}>{s}</p>
                      {at?.at && <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-mutedc">{new Date(at.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 border border-linec bg-white p-6" data-testid="track-details">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-mutedc">Delivering to</p>
                  <p className="mt-2 text-sm leading-relaxed">{order.customer_name}<br />{order.address1}<br />{order.city}, {order.state} {order.pincode}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-mutedc">Courier</p>
                  <p className="mt-2 text-sm">{order.courier || "Courier partner assigned at dispatch"}</p>
                  {order.tracking_number && <p className="mt-1 font-mono text-xs text-crimson">AWB: {order.tracking_number}</p>}
                </div>
              </div>
              <div className="mt-6 space-y-3 border-t border-linec pt-5">
                {order.items.map((i) => (
                  <div key={`${i.slug}:${i.size}`} className="flex items-center gap-4">
                    <img src={img(i.image, 120)} alt={i.name} className="h-14 w-12 object-cover" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold">{i.name}</p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-mutedc">Size {i.size} × {i.qty}</p>
                    </div>
                    <p className="text-sm font-semibold">{fmt(i.price_inr * i.qty, i.price_usd * i.qty)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!order && !error && (
          <div className="mt-16 flex items-center gap-4 border border-linec bg-white p-6 text-mutedc" data-testid="track-hint">
            <PackageSearch size={26} strokeWidth={1.4} className="shrink-0 text-crimson" />
            <p className="text-sm leading-relaxed">Your Order ID was shared at checkout and in your confirmation email — it looks like <span className="font-mono font-semibold text-ink">JK-12345</span>.</p>
          </div>
        )}
      </div>
    </div>
  );
}
